import { useToken } from "@hightouch/htui";
import { stagger, useAnimate } from "motion/react";
import { useEffect } from "react";
import { GraphType } from "@hightouch/lib/query/visual/types";
import {
  LineChartLoader,
  NumberChartLoader,
  ColumnChartLoader,
  BarChartLoader,
  TableChartLoader,
  HeatMapChartLoader,
  AreaChartLoader,
} from "src/components/charts/loaders";

export interface ChartLoaderProps {
  graphType?: GraphType;
}

export function getChartLoader({ graphType }: ChartLoaderProps) {
  switch (graphType) {
    case GraphType.Area:
      return <AreaChartLoader />;
    case GraphType.Bar:
      return <BarChartLoader />;
    case GraphType.Column:
      return <ColumnChartLoader />;
    case GraphType.HeatMap:
      return <HeatMapChartLoader />;
    case GraphType.Line:
      return <LineChartLoader />;
    case GraphType.BigNumber:
      return <NumberChartLoader />;
    case GraphType.Table:
      return <TableChartLoader />;
    default:
      return <LineChartLoader />;
  }
}

/**
 * Shared hook for pulse animation with stagger effect.
 *
 * To use, apply the `scope` ref to the container element of the area to animate.
 * Then, apply the `className` to all elements you want to animate.
 *
 * @returns An object containing the ref (scope) and className to use
 */
export const usePulseStaggerAnimation = ({
  duration = 0.95,
  staggerDelay = 0.15,
}: {
  duration?: number;
  staggerDelay?: number;
} = {}) => {
  const [scope, animate] = useAnimate();
  const className = "ht-pulse-stagger";

  const gray200 = useToken("colors", "gray.200");
  const gray50 = useToken("colors", "gray.50");

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    animate(
      `.${className}`,
      {
        fill: [gray200, gray50],
        backgroundColor: [gray200, gray50],
      },
      {
        duration,
        ease: "easeOut",
        repeat: Infinity,
        repeatType: "mirror",
        delay: stagger(staggerDelay),
      },
    );
    // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression
  }, [duration, staggerDelay, animate]);

  return { scope, className };
};

/**
 * Returns motion variants for a trace animation. Used in line and area chart loaders.
 */
export const getTraceVariants = ({
  delay = 0.5,
  duration = 5,
  repeatDelay = 0.5,
  pathLength = 0.11,
}: {
  delay?: number;
  duration?: number;
  repeatDelay?: number;
  pathLength?: number;
} = {}) => {
  return {
    initial: {
      pathLength,
      pathOffset: 0,
    },
    animate: {
      pathLength,
      pathOffset: 1,
      transition: {
        delay,
        duration,
        repeat: Infinity,
        repeatDelay,
      },
    },
  };
};
